import { useReduxState } from 'hooks';
import {
  ContentLabel,
  ContentValue,
  DisplayAttachments,
  DisplayFormattedDate,
  RichTextValue,
  TextWithLineBreaks,
  User
} from 'components/common';
import { AttributeDisplayValue } from 'components/common/customAttributes';
import { AchievementRenderAddedBy, AchievementSimpleStatus } from '.';

import type { AchievementData, UserWithBasicData } from 'shared-types';

export type AchievementCompletionItem = {
  admin?: UserWithBasicData;
  date: string;
  text?: string;
};

interface Props {
  achievementData: AchievementData;
  members: UserWithBasicData[];
}

const AchievementGroupOfUsersCompletionData = ({
  achievementData,
  members
}: Props) => {
  const {
    language,
    messages,
    pageState: { achievement },
    theme
  } = useReduxState();

  return (
    <div className="summary">
      {achievement.description ? (
        <ContentLabel horizontal={true} label={messages.commonDescription}>
          <RichTextValue html={achievement.description} />
        </ContentLabel>
      ) : null}

      <ContentLabel horizontal={true} label={messages.commonParticipants}>
        <div className="users">
          {members.map((member) => (
            <User key={member.id} enableProfileLink={true} user={member} />
          ))}
        </div>
      </ContentLabel>

      <ContentLabel
        horizontal={true}
        label={messages.commonStatus}
        labelStyle={{ paddingTop: 30 }}
      >
        <AchievementSimpleStatus
          expirationDate={achievementData?.expirationDate as string}
          isInactive={achievementData.isInactive}
          status={achievementData.status}
          type={achievement.type}
        />
      </ContentLabel>

      {achievementData?.startedAtDate ? (
        <ContentLabel
          horizontal={true}
          label={messages.commonStartDate}
          style={{ whiteSpace: 'nowrap' }}
        >
          <ContentValue style={{ fontWeight: 600 }}>
            <DisplayFormattedDate date={achievementData.startedAtDate} />
          </ContentValue>
        </ContentLabel>
      ) : null}

      {achievementData?.status === 'completed' ? (
        <ContentLabel
          horizontal={true}
          label={messages.completionsCompletionDate}
          style={{ whiteSpace: 'nowrap' }}
        >
          <ContentValue style={{ fontWeight: 600 }}>
            <DisplayFormattedDate date={achievementData.completionDate} />
          </ContentValue>
        </ContentLabel>
      ) : null}

      {achievementData?.expirationDate ? (
        <ContentLabel
          horizontal={true}
          label={messages.commonExpirationDate}
          style={{ whiteSpace: 'nowrap' }}
        >
          <ContentValue style={{ fontWeight: 600 }}>
            <DisplayFormattedDate date={achievementData.expirationDate} />
          </ContentValue>
        </ContentLabel>
      ) : null}

      <ContentLabel horizontal={true} label={messages.commonAddedBy}>
        <ContentValue style={{ fontWeight: 600 }}>
          <AchievementRenderAddedBy
            addedBy={achievementData?.addedBy}
            enableLink={true}
          />
        </ContentValue>
      </ContentLabel>

      {achievementData?.comments ? (
        <ContentLabel horizontal={true} label={messages.commonComment}>
          <div className="comment">
            <TextWithLineBreaks text={achievementData.comments} />
          </div>
        </ContentLabel>
      ) : null}

      {achievement.certificateAllowDownloading ? (
        <ContentLabel horizontal={true} label={messages.commonAttachments}>
          <DisplayAttachments
            attachments={achievementData?.attachments || []}
            isConfidential={!achievementData?.canSeeAttachments}
            renderInPortal={false}
          />
        </ContentLabel>
      ) : null}

      {achievement.userAttributes?.length
        ? achievement.userAttributes.map((attribute, index) => (
            <ContentLabel
              key={attribute.id}
              horizontal={true}
              label={attribute.titleTranslations?.[language] || attribute.title}
              labelStyle={index === 0 ? { paddingTop: 40 } : {}}
            >
              <ContentValue style={{ fontWeight: 600 }}>
                <AttributeDisplayValue
                  value={achievementData?.userAttributes?.[attribute.id]}
                />
              </ContentValue>
            </ContentLabel>
          ))
        : null}

      <style jsx>{`
        .users {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          gap: 20px;
          width: 100%;
        }
        .comment {
          background-color: ${theme.lightGreyLighter};
          border-radius: ${theme.inputBorderRadius};
          font-size: 1.4rem;
          font-weight: 400;
          line-height: 1.5;
          padding: 8px 12px;
        }
      `}</style>
    </div>
  );
};

export default AchievementGroupOfUsersCompletionData;
